import React, { useState, useEffect } from 'react';
import { supabase } from '../global/supabase';
import HamburgerMenu from '../global/menu';
import SignatureCanvas from '../components/SignatureCanvas';
import InventoryModal from '../components/InventoryModal';
import HistoryModal from '../components/HistoryModal';
import '../global/styles.css';

//////////////////////////////////////////////
// EMS EXPIRED CONFIGURATION
//////////////////////////////////////////////
const EMS_EXPIRED_CONFIG = {
  locationId: 'ems-expired',
  locationName: 'EMS Station - Expired Medications',
  tableName: 'ems_station_expired',
  storageFolder: 'ems_expired',

  // Note: Removed hydromorphone, lorazepam, propofol, rocuronium, midazolam2mg_2ml
  substances: [
    'fentanyl',
    'ketamine',
    'etomidate',
    'midazolam10mg_2ml',
    'midazolam5mg_5ml'
  ]
};

// Format substance names for display
function formatSubstanceName(substance) {
  if (substance === 'sealNumber') return 'Seal Number';
  return substance
    .replace(/_/g, ' ')
    .replace(/(\d+)/g, ' $1')
    .replace(/ml/g, 'mL')
    .replace(/\b\w/g, c => c.toUpperCase())
    .trim();
}

//////////////////////////////////////////////
// MAIN EMS EXPIRED COMPONENT
//////////////////////////////////////////////
function EMSExpired({ userInfo, isAdmin = false, onLogout, onNavigate }) {
  const [liveInventory, setLiveInventory] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [showInventoryModal, setShowInventoryModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch the latest inventory record
  useEffect(() => {
    fetchLiveInventory();
  }, []);

  const fetchLiveInventory = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from(EMS_EXPIRED_CONFIG.tableName)
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) {
        console.error('Error fetching live inventory:', error);
        setLoading(false);
        return;
      }

      if (data && data.length > 0) {
        setLiveInventory(data[0]);
        setLastUpdated(new Date(data[0].created_at));
      } else {
        console.log('No inventory records found');
        setLiveInventory(null);
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInventorySubmit = () => {
    fetchLiveInventory();
    setShowInventoryModal(false);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa', padding: '20px' }}>
      {/* Hamburger Menu */}
      <HamburgerMenu
        isAdmin={isAdmin}
        onLogout={onLogout}
        onNavigate={onNavigate}
      />

      {/* Main Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', paddingTop: '20px' }}>

        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '40px'
        }}>
          <h1 style={{
            color: '#0E3293',
            fontSize: '32px',
            fontWeight: '600',
            marginBottom: '10px'
          }}>
            {EMS_EXPIRED_CONFIG.locationName}
          </h1>
          <p style={{ color: '#666', fontSize: '16px' }}>
            Current Inventory Status
          </p>
        </div>

        {/* Last Updated */}
        {lastUpdated && (
          <div style={{
            textAlign: 'center',
            marginBottom: '30px',
            color: '#666',
            fontSize: '14px'
          }}>
            Last Updated: {lastUpdated.toLocaleString('en-US', {
              month: '2-digit',
              day: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })} EST
          </div>
        )}

        {/* Live Inventory Display */}
        <div style={{
          background: 'white',
          border: '1px solid #e0e0e0',
          borderRadius: '15px',
          padding: '30px',
          marginBottom: '30px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
        }}>
          <h2 style={{
            color: '#333',
            fontSize: '20px',
            fontWeight: '600',
            marginBottom: '20px',
            paddingBottom: '10px',
            borderBottom: '2px solid #0E3293'
          }}>
            Current Inventory
          </h2>

          {loading ? (
            <p style={{ textAlign: 'center', color: '#666' }}>Loading...</p>
          ) : (
            <div style={{ display: 'grid', gap: '15px' }}>
              {/* Seal Number */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '15px',
                background: '#f8f9fa',
                borderRadius: '8px'
              }}>
                <span style={{ fontWeight: '600', color: '#333' }}>Seal Number</span>
                <span style={{
                  color: '#0E3293',
                  fontWeight: '600',
                  fontSize: '18px'
                }}>
                  {liveInventory?.seal_number || 'N/A'}
                </span>
              </div>

              {/* Medications */}
              {EMS_EXPIRED_CONFIG.substances.map(substance => (
                <div key={substance} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '15px',
                  background: '#f8f9fa',
                  borderRadius: '8px'
                }}>
                  <span style={{ fontWeight: '500', color: '#333' }}>
                    {formatSubstanceName(substance)}
                  </span>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{
                      color: '#0E3293',
                      fontWeight: '600',
                      fontSize: '18px',
                      marginRight: '10px'
                    }}>
                      {liveInventory?.[substance] || '0'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '20px',
          marginBottom: '30px'
        }}>
          {userInfo?.editor && (
            <button
              className="btn btn-primary"
              onClick={() => setShowInventoryModal(true)}
              style={{ width: '100%', padding: '15px' }}
            >
              Change Inventory
            </button>
          )}

          <button
            className="btn btn-secondary"
            onClick={() => setShowHistoryModal(true)}
            style={{ width: '100%', padding: '15px' }}
          >
            View Inventory Logs
          </button>
        </div>

      </div>

      {/* Inventory Modal */}
      {showInventoryModal && (
        <InventoryModal
          config={EMS_EXPIRED_CONFIG}
          userInfo={userInfo}
          lastInventory={liveInventory}
          onClose={() => setShowInventoryModal(false)}
          onSubmit={handleInventorySubmit}
        />
      )}

      {/* History Modal */}
      {showHistoryModal && (
        <HistoryModal
          config={EMS_EXPIRED_CONFIG}
          onClose={() => setShowHistoryModal(false)}
        />
      )}
    </div>
  );
}

export default EMSExpired;
